import { inject, Injectable, computed } from '@angular/core';
import { take } from 'rxjs';
import { TrackingEventType } from '@newroom-connect/library/enums';

import { EntityService } from '../entity/entity.service';
import { LoggingService } from '../logging/logging.service';
import { SessionService } from '../session/session.service';
import { LanguageService } from '../language/language.service';
import { ProjectService } from '../project/project.service';
import { AreaService } from '../area/area.service';
import { DeviceInformationService } from '../device-information/device-information.service';

export interface ICreateTrackingEventInput {
  eventType: TrackingEventType;
  duration?: number;
  deviceInfo?: string;
  country?: string;
  region?: string;
  city?: string;
  metadata?: string;
  areaElementId?: string;
  fileId?: string;
  actionId?: string;
  mimetype?: string;
}

@Injectable({
  providedIn: 'root'
})
export class TrackingEventService extends EntityService {
  private readonly logger = inject(LoggingService);
  private readonly sessionService = inject(SessionService);
  private readonly languageService = inject(LanguageService);
  private readonly projectService = inject(ProjectService);
  private readonly areaService = inject(AreaService);
  private readonly deviceInformationService = inject(DeviceInformationService);

  public searchableProperties = [];

  // Computed signals for common tracking data
  private readonly sessionId = computed(() => {
    return this.sessionService.getUserSession()?.getSessionId() ?? null;
  });

  private readonly languageCode = computed(() => {
    return this.languageService.watchSelectedLanguageCode()() ?? '';
  });

  private readonly currentProjectId = computed(() => {
    return this.projectService.watchCurrentProject()()?.id;
  });

  private readonly currentAreaId = computed(() => {
    return this.areaService.currentArea()?.id;
  });

  /**
   * Creates tracking events using centrally managed common data.
   *
   * @param input
   */
  public createTrackingEvent(input: ICreateTrackingEventInput): void {
    const sessionId = this.sessionId();
    const languageCode = this.languageCode();
    const projectId = this.currentProjectId();
    const areaId = this.currentAreaId();
    const deviceInfo =  JSON.stringify({
      device: this.deviceInformationService.getDeviceName(),
      browser: this.deviceInformationService.getBrowser()
    });

    if (!sessionId || !projectId) {
      return;
    }

    // Prepare tracking event data
    const trackingEventData = {
      ...input,
      sessionId,
      languageCode,
      projectId,
      areaId,
      deviceInfo
    };

    this.apiService.post(`projects/${projectId}/tracking-events`, trackingEventData).pipe(take(1)).subscribe({
      error: (error) => {
        this.logger.error('Error creating tracking event', error);
      }
    });
  }
}
